import React, { useState, useEffect, useMemo, useImperativeHandle } from 'react';
import {
  Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ClearIcon from '@mui/icons-material/Clear';
import EditIcon from '@mui/icons-material/Edit';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { InvTextField, InvSelect } from '../InvField';
import StockBalanceAutocomplete from '../StockBalanceAutocomplete';
import SelectPatientDialog from './SelectPatientDialog';
import { createConsumption } from '../../api/dispensing';
import { getAllocatedAdmissions } from '../../../ipd/pages/patient-admission-ipd/api/registrationService';
import { ENCOUNTER_TYPES } from '../../constants/storeManagement';
import { PRIORITY_LEVELS } from '../../constants';
import { generateTxnNumber, formatDate } from '../../utils';
import { IPD_COLORS } from '../../theme';

let nextGroupId = 1;

const ENTRY_DEFAULT = {
  rowType: 'entry',
  groupId: '',
  item_key: '',
  item_id: '',
  item_name: '',
  item_code: '',
  uom_id: '',
  batch_number: '',
  grn_item_id: '',
  stock_balance_id: '',
  total_available_quantity: '',
  available_quantity: '',
  mrp: '',
  sale_price: '',
  expiry_date: '',
  requested_quantity: '',
  _catalogue: null,
};

function newEntryRow() {
  return { ...ENTRY_DEFAULT, groupId: `g-${nextGroupId++}` };
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Split requested qty across batches by ascending fefo_rank. Returns null if cannot fulfill. */
function allocateFefoBatches(catalogueItem, requestedQty) {
  const requested = toNumber(requestedQty);
  const totalAvail = toNumber(catalogueItem?.total_available_quantity);
  if (requested == null || requested <= 0) return [];
  if (totalAvail == null || requested > totalAvail) return null;

  const batches = [...(catalogueItem?.batches || [])]
    .filter(b => !b.is_expired && toNumber(b.available_quantity) > 0)
    .sort((a, b) => (Number(a.fefo_rank) || 999) - (Number(b.fefo_rank) || 999));

  let remaining = requested;
  const picks = [];
  for (const batch of batches) {
    if (remaining <= 0) break;
    const avail = toNumber(batch.available_quantity) || 0;
    const take = Math.min(avail, remaining);
    if (take <= 0) continue;
    picks.push({ batch, quantity: take });
    remaining = Number((remaining - take).toFixed(6));
  }

  if (remaining > 0.000001) return null;
  return picks;
}

function buildBatchRows(entry, picks) {
  const catalogue = entry._catalogue;
  return picks.map((pick) => ({
    rowType: 'batch',
    groupId: entry.groupId,
    item_key: entry.item_key || catalogue?.item_id || '',
    item_id: catalogue.item_id,
    item_name: catalogue.item_name || '',
    item_code: catalogue.item_code || '',
    uom_id: catalogue.base_uom_id || '',
    batch_number: pick.batch.batch_number || '',
    grn_item_id: pick.batch.grn_item_id || '',
    stock_balance_id: pick.batch.stock_balance_id || '',
    total_available_quantity: catalogue.total_available_quantity ?? '',
    available_quantity: pick.batch.available_quantity ?? '',
    mrp: pick.batch.mrp ?? '',
    sale_price: pick.batch.sale_price ?? pick.batch.mrp ?? '',
    expiry_date: pick.batch.expiry_date ?? '',
    requested_quantity: String(pick.quantity),
    _catalogue: catalogue,
  }));
}

function entryQuantityError(line) {
  if (line.rowType !== 'entry' || !line.item_id) return undefined;
  const requested = toNumber(line.requested_quantity);
  const available = toNumber(line.total_available_quantity);
  if (requested == null || requested <= 0 || available == null) return undefined;
  if (requested > available) {
    return { message: `Exceeds available quantity (${available})` };
  }
  return undefined;
}

function lineSalePrice(line) {
  return toNumber(line.sale_price) || toNumber(line.mrp);
}

function formatPrice(value) {
  const n = toNumber(value);
  if (n == null) return '—';
  return n.toFixed(2);
}

function formatAmount(rate, qty) {
  const r = toNumber(rate);
  const q = toNumber(qty);
  if (r == null || q == null) return '—';
  return (r * q).toFixed(2);
}

/** Quantity and value of one item across every batch FEFO allocated it to. */
function groupTotals(batches) {
  const totals = batches.reduce((acc, line) => {
    const qty = toNumber(line.requested_quantity) || 0;
    acc.qty += qty;
    acc.amount += (lineSalePrice(line) || 0) * qty;
    return acc;
  }, { qty: 0, amount: 0 });
  // Splitting a quantity across batches accumulates float error.
  return { qty: Number(totals.qty.toFixed(6)), amount: totals.amount };
}

/** The group's single rate, or null when its batches were priced differently. */
function groupSaleRate(batches) {
  const rates = batches.map(lineSalePrice);
  const first = rates.length ? rates[0] : null;
  return rates.every(rate => rate === first) ? first : null;
}

function linePayload(line) {
  return {
    item_id: line.item_id,
    uom_id: line.uom_id || undefined,
    requested_quantity: Number(line.requested_quantity),
    unit_rate: lineSalePrice(line) ?? 0,
    tax_percent: 0,
    discount_percent: 0,
    batch_number: line.batch_number || undefined,
    grn_item_id: line.grn_item_id || undefined,
  };
}

export default function StoreConsumptionForm({
  storeId,
  storeName,
  consumptionType = 'WARD',
  onCancel,
  onSuccess,
  // Optional controlled patient — when the caller already selects a patient
  // elsewhere (e.g. OT Charge Capture's shared top-of-form picker), pass it
  // here so this form reflects that same patient instead of running its own
  // independent search. Omit (undefined) to keep the original behavior.
  patient,
  hideSubmit = false,
  formApiRef,
}) {
  const isPatient = consumptionType === 'PATIENT';
  const isControlledPatient = patient !== undefined;
  const [saving, setSaving] = useState(false);
  const [lines, setLines] = useState([newEntryRow()]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(patient || null);
  const [admissions, setAdmissions] = useState([]);
  const [admissionsLoading, setAdmissionsLoading] = useState(false);
  const [admissionsError, setAdmissionsError] = useState(null);

  const { control, handleSubmit, formState: { errors }, getValues } = useForm({
    defaultValues: {
      transaction_number: generateTxnNumber('CON'),
      transaction_date: dayjs().format('YYYY-MM-DD'),
      priority: 'NORMAL',
      remarks: '',
      encounter_type: 'IP',
    },
  });

  // Controlled mode: reflect the caller's patient whenever it changes.
  useEffect(() => {
    if (isControlledPatient) setSelectedPatient(patient || null);
  }, [isControlledPatient, patient]);

  // Item stock is store-specific — wipe lines when the store changes.
  useEffect(() => {
    setLines([newEntryRow()]);
  }, [storeId]);

  // Load allocated IPD admissions once when opening Patient Consumption —
  // only needed for the uncontrolled (own search) flow.
  useEffect(() => {
    if (!isPatient || isControlledPatient) return undefined;
    let cancelled = false;
    setAdmissionsLoading(true);
    setAdmissionsError(null);
    getAllocatedAdmissions()
      .then((list) => {
        if (!cancelled) setAdmissions(list);
      })
      .catch((e) => {
        if (!cancelled) {
          setAdmissions([]);
          setAdmissionsError(e.message || 'Failed to load allocated patients');
        }
      })
      .finally(() => {
        if (!cancelled) setAdmissionsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isPatient, isControlledPatient]);

  /**
   * One render row per item, not per allocated batch.
   *
   * `lines` stays flat (entry + batch rows keyed by groupId) because FEFO
   * allocation and the submit payload are built from it. Only the table is
   * grouped, so an item that draws on three batches reads as one line with a
   * breakdown rather than three look-alike rows.
   */
  const groupRows = useMemo(() => {
    const order = [];
    const byId = new Map();
    lines.forEach((line, index) => {
      let group = byId.get(line.groupId);
      if (!group) {
        group = { groupId: line.groupId, entry: null, entryIndex: -1, batches: [], lastIndex: index };
        byId.set(line.groupId, group);
        order.push(group);
      }
      group.lastIndex = index;
      if (line.rowType === 'entry') {
        group.entry = line;
        group.entryIndex = index;
      } else {
        group.batches.push(line);
      }
    });
    return order;
  }, [lines]);

  function toggleGroup(groupId) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }

  function insertRowAfter(index) {
    setLines(prev => [
      ...prev.slice(0, index + 1),
      newEntryRow(),
      ...prev.slice(index + 1),
    ]);
  }

  function applySelectedPatient(patient) {
    setSelectedPatient(patient);
  }

  function clearSelectedPatient() {
    setSelectedPatient(null);
  }

  function selectCatalogueItem(index, itemKey, catalogueItem) {
    setLines(prev => {
      const line = prev[index];
      if (!line || line.rowType !== 'entry') return prev;

      if (!catalogueItem) {
        return prev.map((row, i) => (i === index ? { ...newEntryRow(), groupId: line.groupId } : row));
      }

      const nextEntry = {
        ...line,
        rowType: 'entry',
        item_key: itemKey || catalogueItem.item_id,
        item_id: catalogueItem.item_id,
        item_name: catalogueItem.item_name || '',
        item_code: catalogueItem.item_code || '',
        uom_id: catalogueItem.base_uom_id || '',
        batch_number: '',
        grn_item_id: '',
        stock_balance_id: '',
        total_available_quantity: catalogueItem.total_available_quantity ?? '',
        available_quantity: catalogueItem.total_available_quantity ?? '',
        requested_quantity: line.requested_quantity || '',
        _catalogue: catalogueItem,
      };

      // Keep as editable entry until quantity blur — don't auto-expand on item select alone.
      return prev.map((row, i) => (i === index ? nextEntry : row));
    });
  }

  function updateEntryQuantity(index, value) {
    setLines(prev => prev.map((row, i) => (
      i === index && row.rowType === 'entry'
        ? { ...row, requested_quantity: value }
        : row
    )));
  }

  function applyFefoForEntry(index) {
    setLines(prev => {
      const line = prev[index];
      if (!line || line.rowType !== 'entry' || !line._catalogue) return prev;

      const requested = toNumber(line.requested_quantity);
      const available = toNumber(line.total_available_quantity);
      if (requested == null || requested <= 0) return prev;
      if (available != null && requested > available) return prev;

      const picks = allocateFefoBatches(line._catalogue, requested);
      if (!picks || !picks.length) return prev;

      const without = prev.filter((_, i) => i !== index);
      return [
        ...without.slice(0, index),
        ...buildBatchRows(line, picks),
        ...without.slice(index),
      ];
    });
  }

  function reopenGroupAsEntry(groupId) {
    setLines(prev => {
      const groupRows = prev.filter(l => l.groupId === groupId);
      if (!groupRows.length) return prev;
      const head = groupRows[0];
      const totalRequested = groupRows.reduce((sum, row) => sum + (toNumber(row.requested_quantity) || 0), 0);
      const entry = {
        ...ENTRY_DEFAULT,
        groupId,
        item_key: head.item_key || head.item_id,
        item_id: head.item_id,
        item_name: head.item_name,
        item_code: head.item_code,
        uom_id: head.uom_id,
        total_available_quantity: head.total_available_quantity,
        available_quantity: head.total_available_quantity,
        requested_quantity: String(totalRequested || ''),
        _catalogue: head._catalogue,
      };
      const start = prev.findIndex(l => l.groupId === groupId);
      const withoutGroup = prev.filter(l => l.groupId !== groupId);
      return [
        ...withoutGroup.slice(0, start),
        entry,
        ...withoutGroup.slice(start),
      ];
    });
  }

  function removeGroup(groupId) {
    setLines(prev => {
      const next = prev.filter(l => l.groupId !== groupId);
      return next.length ? next : [newEntryRow()];
    });
  }

  async function onSubmit(data) {
    const pendingEntries = lines.filter(l => l.rowType === 'entry' && l.item_id && toNumber(l.requested_quantity) > 0);
    if (pendingEntries.some(l => entryQuantityError(l))) {
      toast.error('Requested quantity cannot exceed available stock');
      return;
    }
    if (pendingEntries.length) {
      toast.error('Finish item quantities so batches can be allocated');
      return;
    }

    const validLines = lines.filter(l => l.rowType === 'batch' && l.item_id && Number(l.requested_quantity) > 0);
    if (!validLines.length) {
      toast.error('Add at least one item with quantity');
      return;
    }
    if (isPatient && !selectedPatient?.patient_id) {
      toast.error('Select a patient for patient consumption');
      return;
    }

    setSaving(true);
    try {
      const body = {
        transaction_number: data.transaction_number,
        from_store_id: storeId,
        consumption_type: consumptionType,
        transaction_date: data.transaction_date || undefined,
        priority: data.priority || 'NORMAL',
        remarks: data.remarks || undefined,
        items: validLines.map(linePayload),
      };

      if (isPatient) {
        body.patient_id = selectedPatient.patient_id;
        body.encounter_type = data.encounter_type || undefined;
        if (selectedPatient.admission_id) body.admission_id = selectedPatient.admission_id;
        if (selectedPatient.billing_account_id) body.billing_account_id = selectedPatient.billing_account_id;
      }

      // API: POST /inventory/dispensing/consumption/
      await createConsumption(body);
      toast.success(`${isPatient ? 'Patient' : 'Ward'} consumption created`);
      onSuccess?.();
    } catch (e) {
      toast.error(e.message || 'Failed to create consumption');
    } finally {
      setSaving(false);
    }
  }

  const encounterOptions = ENCOUNTER_TYPES.map(v => ({ value: v, label: v }));
  const priorityOptions = PRIORITY_LEVELS.map(v => ({ value: v, label: v }));
  const hasEntryErrors = lines.some(l => Boolean(entryQuantityError(l)));
  const hasBatchItems = lines.some(l => l.rowType === 'batch' && l.item_id && Number(l.requested_quantity) > 0);
  const canSubmit = !hasEntryErrors
    && hasBatchItems
    && (!isPatient || Boolean(selectedPatient?.patient_id));

  function collectConsumption() {
    const pendingEntries = lines.filter(l => l.rowType === 'entry' && l.item_id && toNumber(l.requested_quantity) > 0);
    if (pendingEntries.some(l => entryQuantityError(l))) {
      return { error: 'Requested quantity cannot exceed available stock' };
    }
    if (pendingEntries.length) {
      return { error: 'Finish item quantities so batches can be allocated' };
    }
    const validLines = lines.filter(l => l.rowType === 'batch' && l.item_id && Number(l.requested_quantity) > 0);
    if (!validLines.length) return { skipped: true };
    if (!storeId) return { error: 'Select a store for patient consumption' };
    if (isPatient && !selectedPatient?.patient_id) {
      return { error: 'Select a patient for patient consumption' };
    }
    const data = getValues();
    const body = {
      transaction_number: data.transaction_number,
      from_store_id: storeId,
      consumption_type: consumptionType,
      transaction_date: data.transaction_date || undefined,
      priority: data.priority || 'NORMAL',
      remarks: data.remarks || undefined,
      items: validLines.map(linePayload),
    };
    if (isPatient) {
      body.patient_id = selectedPatient.patient_id;
      body.encounter_type = data.encounter_type || undefined;
      if (selectedPatient.admission_id) body.admission_id = selectedPatient.admission_id;
      if (selectedPatient.billing_account_id) body.billing_account_id = selectedPatient.billing_account_id;
    }
    return { body, lines: validLines };
  }

  useImperativeHandle(formApiRef, () => ({
    collect: collectConsumption,
    reset() {
      setLines([newEntryRow()]);
    },
  }), [
    lines, storeId, consumptionType, selectedPatient, isPatient, getValues,
  ]);

  const actionButtons = (
    <>
      <InvButton variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={onCancel}>
        Back to list
      </InvButton>
      {!hideSubmit && (
        <InvButton type="submit" size="small" loading={saving} disabled={!canSubmit}>
          Create consumption
        </InvButton>
      )}
    </>
  );

  return (
    <Box component="form" onSubmit={hideSubmit ? (e) => e.preventDefault() : handleSubmit(onSubmit)}>
      {!hideSubmit && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" sx={{ color: IPD_COLORS.textSecondary, letterSpacing: 1 }}>
              {isPatient ? 'Patient Consumption' : 'Ward Consumption'}
            </Typography>
            <Typography sx={{ fontWeight: 600, color: IPD_COLORS.textPrimary }}>
              New consumption{storeName ? ` · ${storeName}` : ''}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actionButtons}
          </Box>
        </Box>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <SectionCard title="Consumption details">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="transaction_number"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvTextField {...field} label="Consumption # *" error={errors.transaction_number} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="transaction_date"
                  control={control}
                  render={({ field }) => (
                    <InvTextField {...field} label="Date" type="date" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <InvSelect label="Priority" value={field.value} onChange={field.onChange} options={priorityOptions} />
                  )}
                />
              </Grid>
              {isPatient && isControlledPatient && (
                <Grid item xs={12} sm={6} md={4}>
                  <Controller
                    name="encounter_type"
                    control={control}
                    render={({ field }) => (
                      <InvSelect
                        label="Encounter type"
                        value={field.value}
                        onChange={field.onChange}
                        options={encounterOptions}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Controller
                  name="remarks"
                  control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />}
                />
              </Grid>
            </Grid>
          </SectionCard>

          {isPatient && !isControlledPatient && (
            <SectionCard
              title="Patient"
              sx={{ mt: 2.5 }}
              actions={!isControlledPatient ? (
                <InvButton
                  size="small"
                  startIcon={<PersonSearchIcon />}
                  onClick={() => setSearchOpen(true)}
                >
                  {selectedPatient ? 'Change patient' : 'Search patient'}
                </InvButton>
              ) : undefined}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  {selectedPatient ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        px: 1.5,
                        py: 1.25,
                        border: `1px solid ${IPD_COLORS.border}`,
                        borderRadius: 1,
                      }}
                    >
                      <Box>
                        <Typography sx={{ color: IPD_COLORS.textPrimary, fontWeight: 600, fontSize: 14 }}>
                          {selectedPatient.name || '—'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                          UHID: {selectedPatient.uhid || '—'}
                          {' · '}
                          Gender: {selectedPatient.gender || '—'}
                          {' · '}
                          Age: {selectedPatient.age ? `${selectedPatient.age} yrs` : '—'}
                        </Typography>
                      </Box>
                      {!isControlledPatient && (
                        <IconButton
                          size="small"
                          aria-label="Clear patient"
                          onClick={clearSelectedPatient}
                          sx={{ color: IPD_COLORS.textSecondary }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        border: `1px dashed ${IPD_COLORS.border}`,
                        borderRadius: 1,
                      }}
                    >
                      <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 14 }}>
                        {isControlledPatient ? 'Select a patient above to continue' : 'Search and select a patient to continue'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Controller
                    name="encounter_type"
                    control={control}
                    render={({ field }) => (
                      <InvSelect
                        label="Encounter type"
                        value={field.value}
                        onChange={field.onChange}
                        options={encounterOptions}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          )}

          <SectionCard title="Items" sx={{ mt: 2.5 }}>
            <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block', mb: 1.5 }}>
              Quantities are allocated across batches by FEFO (earliest expiry / fefo_rank first). Batch numbers are set automatically.
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 220 }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Batch</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Sale Price</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 90 }}>Avail. Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Quantity *</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Amount</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 110 }}>Expiry</TableCell>
                    <TableCell sx={{ width: 110 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupRows.map((group) => {
                    if (group.entry) {
                      const line = group.entry;
                      const entryIndex = group.entryIndex;
                      return (
                        <TableRow key={`${group.groupId}-entry`}>
                          <TableCell>
                            <StockBalanceAutocomplete
                              storeId={storeId}
                              value={line.item_key}
                              onChange={(key, item) => selectCatalogueItem(entryIndex, key, item)}
                              placeholder="Search item (min 2 chars)…"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
                              Auto (FEFO)
                            </Typography>
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell>
                            <InvTextField
                              value={line.requested_quantity}
                              label=""
                              onChange={e => updateEntryQuantity(entryIndex, e.target.value)}
                              onBlur={() => applyFefoForEntry(entryIndex)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  applyFefoForEntry(entryIndex);
                                }
                              }}
                              type="number"
                              inputProps={{ min: 0.01, step: 0.01 }}
                              error={entryQuantityError(line)}
                            />
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              <IconButton
                                size="small"
                                aria-label="Delete row"
                                onClick={() => removeGroup(line.groupId)}
                                sx={{ color: IPD_COLORS.error.text }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              <Tooltip title="Add row">
                                <IconButton
                                  size="small"
                                  aria-label="Add row below"
                                  onClick={() => insertRowAfter(group.lastIndex)}
                                  sx={{ color: IPD_COLORS.primary }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    const head = group.batches[0];
                    const totals = groupTotals(group.batches);
                    const rate = groupSaleRate(group.batches);
                    const extraBatches = group.batches.length - 1;
                    const expanded = expandedGroups.has(group.groupId);

                    return (
                      <React.Fragment key={group.groupId}>
                        <TableRow>
                          <TableCell>
                            <Typography sx={{ fontSize: 14, color: IPD_COLORS.textPrimary }}>
                              {head.item_name || '—'}
                              {head.item_code ? (
                                <Typography component="span" sx={{ color: IPD_COLORS.textSecondary, fontSize: 12 }}>
                                  {` (${head.item_code})`}
                                </Typography>
                              ) : null}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <InvTextField value={head.batch_number || '—'} label="" disabled />
                              {extraBatches > 0 && (
                                <Tooltip title={expanded ? 'Hide batch split' : `Split across ${group.batches.length} batches`}>
                                  <IconButton
                                    size="small"
                                    aria-label="Toggle batch split"
                                    onClick={() => toggleGroup(group.groupId)}
                                    sx={{ color: IPD_COLORS.primary }}
                                  >
                                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            {extraBatches > 0 && (
                              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.25 }}>
                                {`+${extraBatches} more batch${extraBatches > 1 ? 'es' : ''}`}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                              {rate == null ? 'Mixed' : formatPrice(rate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                              {head.total_available_quantity || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <InvTextField value={String(totals.qty)} label="" disabled />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                              {totals.amount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: IPD_COLORS.textPrimary }}>
                              {head.expiry_date ? formatDate(head.expiry_date) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                              <IconButton
                                size="small"
                                title="Edit quantity"
                                aria-label="Edit quantity"
                                onClick={() => reopenGroupAsEntry(group.groupId)}
                                sx={{ color: IPD_COLORS.primary }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Delete row"
                                onClick={() => removeGroup(group.groupId)}
                                sx={{ color: IPD_COLORS.error.text }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                              <Tooltip title="Add row">
                                <IconButton
                                  size="small"
                                  aria-label="Add row below"
                                  onClick={() => insertRowAfter(group.lastIndex)}
                                  sx={{ color: IPD_COLORS.primary }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {extraBatches > 0 && expanded && (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ backgroundColor: IPD_COLORS.panelHeaderBg, py: 1 }}>
                              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary, mb: 0.75 }}>
                                FEFO split — the earliest-expiry batch is consumed first.
                              </Typography>
                              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border } }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Batch</TableCell>
                                    <TableCell sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Expiry</TableCell>
                                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Avail. Qty</TableCell>
                                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Sale Price</TableCell>
                                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Qty</TableCell>
                                    <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Amount</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.batches.map((line, bi) => (
                                    <TableRow
                                      key={line.stock_balance_id || `${line.batch_number}-${bi}`}
                                      sx={bi === 0 ? { backgroundColor: IPD_COLORS.cardBg } : undefined}
                                    >
                                      <TableCell sx={{ fontSize: 12, fontWeight: bi === 0 ? 700 : 400 }}>
                                        {line.batch_number || '—'}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: 12 }}>
                                        {line.expiry_date ? formatDate(line.expiry_date) : '—'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontSize: 12 }}>
                                        {line.available_quantity || '—'}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontSize: 12 }}>
                                        {formatPrice(lineSalePrice(line))}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontSize: 12 }}>
                                        {line.requested_quantity}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontSize: 12 }}>
                                        {formatAmount(lineSalePrice(line), line.requested_quantity)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>

          {!hideSubmit && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2.5, flexWrap: 'wrap' }}>
              {actionButtons}
            </Box>
          )}
        </Grid>
      </Grid>

      {isPatient && !isControlledPatient && (
        <SelectPatientDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSelect={applySelectedPatient}
          admissions={admissions}
          loading={admissionsLoading}
          error={admissionsError}
        />
      )}
    </Box>
  );
}
